import * as React from "react";
import clsx from "clsx";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import {
  button,
  content,
  contentIconOnly,
  iconWrap,
  spinner,
  spinnerOverlay,
} from "./Button.style.css";
import type { ButtonColor, ButtonVariant, ButtonSize } from "./Button.types";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: ButtonColor;
  variant?: ButtonVariant;
  size?: ButtonSize;
  pressed?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  /** Circular icon-only button (equal width/height, fully rounded). */
  iconOnly?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const ICON_PX: Record<ButtonSize, number> = { sm: 16, md: 18, lg: 20 };

function renderIcon(node: ReactNode, size: ButtonSize) {
  if (!node) return null;
  const px = ICON_PX[size];
  if (React.isValidElement(node)) {
    return React.cloneElement(node as React.ReactElement, {
      width: px,
      height: px,
      "aria-hidden": true,
      focusable: false,
      ...((node as any).props ?? {}),
      style: {
        display: "block",
        ...(node as any).props?.style,
        width: px,
        height: px,
      },
    });
  }
  return node;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      color = "primary",
      variant = "solid",
      size = "md",
      pressed,
      loading = false,
      fullWidth = false,
      iconOnly = false,
      disabled,
      iconLeft,
      iconRight,
      className,
      children,
      ...rest
    },
    ref
  ) {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        data-pressed={pressed ? "true" : undefined}
        data-loading={loading ? "true" : undefined}
        aria-busy={loading || undefined}
        disabled={isDisabled}
        className={clsx(
          button({
            color: color as ButtonColor,
            variant: variant as ButtonVariant,
            size: size as ButtonSize,
            fullWidth,
            iconOnly,
          }),
          className
        )}
        {...rest}
      >
        <span className={clsx(content, iconOnly && contentIconOnly)}>
          {iconLeft ? (
            <span className={iconWrap}>
              {renderIcon(iconLeft, size as ButtonSize)}
            </span>
          ) : null}

          {iconOnly && children != null && children !== false ? (
            <span className={iconWrap}>{renderIcon(children, size as ButtonSize)}</span>
          ) : children != null && children !== false ? (
            <span>{children}</span>
          ) : null}

          {iconRight ? (
            <span className={iconWrap}>
              {renderIcon(iconRight, size as ButtonSize)}
            </span>
          ) : null}
        </span>
        <span className={spinnerOverlay}>
          {loading && (
            <span
              aria-hidden="true"
              className={spinner({ size: size as ButtonSize })}
            />
          )}
        </span>
      </button>
    );
  }
);
